import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __WAQ_PRERENDERING__?: boolean;
  }
}

const configuredSiteKey = () => import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();
let loading: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    const timeout = window.setTimeout(() => fail(), 15_000);
    const fail = () => {
      window.clearTimeout(timeout);
      script.remove();
      loading = undefined;
      reject(new Error('Security check could not load. Please retry or email hello@weareqa.com.'));
    };
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onerror = fail;
    script.onload = () => {
      if (!window.turnstile) return fail();
      window.clearTimeout(timeout);
      resolve(window.turnstile);
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface ChallengeHandle {
  getToken: () => string | undefined;
  reset: () => void;
}

/** One widget per form; expired and consumed tokens must never be reused. */
export const FormChallenge = forwardRef<ChallengeHandle, { action: string }>(function FormChallenge(
  { action },
  ref
) {
  const siteKey = configuredSiteKey();
  const container = useRef<HTMLDivElement>(null);
  const token = useRef('');
  const widget = useRef<string>();
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useImperativeHandle(
    ref,
    () => ({
      getToken: () => {
        if (!siteKey) return undefined;
        if (!token.current) throw new Error('Please complete the security check before sending.');
        return token.current;
      },
      reset: () => {
        token.current = '';
        if (widget.current) window.turnstile?.reset(widget.current);
      },
    }),
    [siteKey]
  );

  useEffect(() => {
    if (!siteKey || window.__WAQ_PRERENDERING__) return;
    let disposed = false;
    setError('');
    const start = () => {
      void loadTurnstile()
        .then((api) => {
          if (disposed || !container.current) return;
          widget.current = api.render(container.current, {
            sitekey: siteKey,
            action,
            theme: 'light',
            size: 'flexible',
            'response-field': false,
            callback: (value: string) => {
              token.current = value;
              setError('');
            },
            'expired-callback': () => {
              token.current = '';
            },
            'error-callback': () => {
              token.current = '';
              setError('Security check failed. Please retry or email hello@weareqa.com.');
            },
          });
        })
        .catch((reason: unknown) => {
          if (!disposed)
            setError(reason instanceof Error ? reason.message : 'Security check could not load.');
        });
    };
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? undefined
        : new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) {
                observer?.disconnect();
                start();
              }
            },
            { rootMargin: '200px' }
          );
    if (observer && container.current) observer.observe(container.current);
    else start();
    return () => {
      observer?.disconnect();
      disposed = true;
      token.current = '';
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = undefined;
    };
  }, [action, attempt, siteKey]);

  if (!siteKey) return null;
  return (
    <div className="grid min-w-0 gap-2">
      <div ref={container} />
      {error && (
        <p role="alert" className="text-note font-semibold text-destructive">
          {error}
        </p>
      )}
      {error && (
        <button
          type="button"
          className="justify-self-start text-note font-semibold text-blue underline"
          onClick={() => setAttempt((value) => value + 1)}
        >
          Retry security check
        </button>
      )}
    </div>
  );
});
